/* eslint-disable max-lines */
import {
  externalLaunchContextAtom,
  ExternalLaunchType,
} from '@/atoms/externalLaunchAuthAtoms';
import { setSidebarOpenTab } from '@/atoms/persistedPreferencesAtoms';
import {
  sessionlessEvidenceAwarenessDevForceShowAtom,
  sessionlessEvidenceAwarenessDevVariantOverrideAtom,
} from '@/atoms/sessionlessEvidenceAwarenessDevAtoms';
import {
  GET_EVIDENCE_AWARENESS_POPUP_VARIANT,
  IS_CALLS_ENABLED,
  IS_CLINICAL_TASKS_ENABLED,
  IS_COMMS_SELF_SERVE_ENABLED,
  IS_INSET_DESIGN_ENABLED,
  IS_LIVE_TRANSCRIPTION_INDICATOR_ENABLED,
  IS_PROTOCOLS_ENABLED,
} from '@/components/constants/flags';
import { useEvidenceNavEnabled } from '@/components/layout/Navigation/hooks/useEvidenceNavEnabled';
import { useIsRouteActive } from '@/components/layout/Navigation/hooks/useIsRouteActive';
import {
  CallsNavItem,
  ConciergeNavItem,
  RefillsNavItem,
  TasksNavItem,
  TriageNavItem,
} from '@/components/layout/Navigation/PrimaryNavItems';
import { TemplateGroupNavItems } from '@/components/layout/Navigation/sidebar/nav-items/TemplateGroupNavItems';
import { SessionlessEvidenceAwarenessPopover } from '@/components/modal/sessionless-evidence-awareness-popover';
import { useWorkspaceTopMenuPorts } from '@/composition/hosts/workspace-top-menu-ports';
import { useWhiteLabelSafe } from '@/context/white-label';
import {
  useIsAgentHomeV1Enabled,
  useIsConciergePocEnabled,
  useIsRepeatsPocEnabled,
  useIsRibbonSidebarEnabled,
  useIsSidebarIAEnabled,
  useIsTriagePocEnabled,
  useIsWorkspaceSwitcherEnabled,
} from '@/features/flags';
import { IS_EVIDENCE_LIBRARY_ENABLED } from '@/features/flags/legacy-flags';
import { useIsEhrLaunch } from '@/hooks/integrations/useIsEhrLaunch';
import { useIsFhirAuth } from '@/hooks/integrations/useIsFhirAuth';
import { useCombinedOrganizationInfo } from '@/hooks/query/useCombinedOrganizationInfo';
import { useCombinedQueriesTeamGeneralInfo } from '@/hooks/query/useCombinedQueriesTeamGeneralInfo';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useTutorial } from '@/hooks/useTutorial';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { trackFormLibraryViewed } from '@/lib/analytics/scribe/form-fill';
import { trackTemplateLibraryViewed } from '@/lib/analytics/scribe/template';
import { track } from '@/lib/analytics/track';
import { cn } from '@/lib/cn';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { CommsEntryPoint } from '@/models/analytics/features/comms';
import { isTauri } from '@/tauri/utils';
import { isOnPrem } from '@/utils/buildProfile';
import { WORK_CHAT_HOME_PATH } from '@heidi/agents/contracts';
import {
  pendingWorkHomeMode,
  pendingWorkHomeViewAtom,
  pendingWorkModeAtom,
  useBaseChatHref,
} from '@heidi/agents/data';
import { Avatar } from '@heidi/design-system/avatar';
import {
  SidebarAction,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  useSidebar,
} from '@heidi/design-system/page-shell';
import { Skeleton } from '@heidi/design-system/skeleton';
import { useTranslations } from '@heidi/runtime/i18n';
import { TemplatePages } from '@heidi/schema/ml-scribe';
import { WorkspaceTopMenu } from '@heidi/workspace';
import { type UserResponse } from '@heidi/workspace/contracts';
import { useQueryWorkspaces } from '@heidi/workspace/data';
import { useAtomValue, useSetAtom } from 'jotai';
import { AudioLines, Blocks, FileText, House, Library, SquareCheck } from 'lucide-react';
import { useRouter } from 'next/router';
import { useCallback, useMemo, useTransition } from 'react';
import { useIntl } from 'react-intl';
import { CreateButton } from './create-button/create-button';
import { HeidiLogo } from './HeidiLogo';
import { usePrimaryNavMoreDescriptors } from './hooks/usePrimaryNavMoreDescriptors';
import { LiveTranscriptionIndicator } from './LiveTranscriptionIndicator';
import { useMobilePanel } from './mobile/use-mobile-panel';
import { isAgentHomeRailActive } from './nav-v3/agent-home-panel-state';
import { navV3Messages } from './nav-v3/nav-v3.messages';
import { NewSessionButton } from './NewSessionButton';
import { NavItem } from './sidebar';
import { CodingDashboardNavItem } from './sidebar/nav-items/CodingDashboardNavItem';
import { EvidenceNavItem } from './sidebar/nav-items/EvidenceNavItem';
import { ExpandableNavItemGroup } from './sidebar/nav-items/ExpandableNavItemGroup';
import { HomeNavItem } from './sidebar/nav-items/HomeNavItem';
import { InboxNavItem } from './sidebar/nav-items/InboxNavItem';
import { LibraryNavItem } from './sidebar/nav-items/LibraryNavItem';
import { PatientsNavItem } from './sidebar/nav-items/PatientsNavItem';
import { PrimaryNavMoreGroup } from './sidebar/nav-items/PrimaryNavMoreGroup';
import { ProtocolsNavItem } from './sidebar/nav-items/ProtocolsNavItem';
import { ScribeNavItem } from './sidebar/nav-items/ScribeNavItem';
import { SettingsGroupNavItems } from './sidebar/nav-items/SettingsGroupNavItems';
import { TranslatorNavItem } from './sidebar/nav-items/TranslatorNavItem';
import { PrimaryNavFixedItem } from './sidebar/primary-nav-rail/primary-nav-fixed-item';
import { PrimaryNavRailProvider } from './sidebar/primary-nav-rail/primary-nav-rail-provider';
import { SideNavFooter } from './SideNavFooter';
import { SideNavHotkeys } from './SideNavHotkeys';

const userDataSelector = (data: UserResponse | undefined) => ({
  first_name: data?.first_name,
  last_name: data?.last_name,
  email: data?.email,
  profile_picture_url: data?.profile_picture_url,
  should_hide_sensitive_info_from_sidebar: data?.should_hide_sensitive_info_from_sidebar,
  should_show_try_scribe_tooltip: data?.should_show_try_scribe_tooltip,
  signup_source: data?.signup_source,
});

/**
 * Loading placeholder while `GET /workspaces` is in flight. Mirrors the
 * `inHeader` trigger's layout and reuses the DS `Avatar` for the circle, so the
 * swap to the real switcher never reflows and the circle can't drift from it.
 * The logo isn't used here because a logo->avatar shape-swap read as a loading
 * bug.
 */
function WorkspaceHeaderSlotSkeleton() {
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';

  return (
    <div
      className={cn(
        'flex h-auto w-full items-center justify-start gap-2 rounded-md p-2',
        isCollapsed && 'mx-auto w-auto justify-center p-0'
      )}
      aria-hidden="true"
      data-testid="workspace-header-slot-skeleton"
    >
      <Avatar>
        <Skeleton className="size-full rounded-full" />
      </Avatar>
      {!isCollapsed && (
        <span className="flex min-w-0 flex-1 flex-col items-start">
          <Skeleton className="h-3.5 w-24 rounded-sm" />
        </span>
      )}
    </div>
  );
}

/**
 * Builds the menu's app ports only where the menu renders, so its settings and
 * router subscriptions stay off `SideNav` itself.
 */
function WorkspaceTopMenuMount({ inHeader }: { inHeader?: boolean }) {
  const ports = useWorkspaceTopMenuPorts();
  return <WorkspaceTopMenu inHeader={inHeader} ports={ports} />;
}

const getDesktopHeaderPaddingClass = ({
  isInsetDesignEnabled,
  isShellAnchored,
}: {
  isInsetDesignEnabled: boolean | undefined;
  isShellAnchored: boolean;
}) => {
  if (isShellAnchored) return isInsetDesignEnabled ? 'pt-4' : 'pt-2';
  return isInsetDesignEnabled ? 'pt-12' : 'pt-10';
};

export function SideNav({ isShellAnchored = false }: { isShellAnchored?: boolean } = {}) {
  const { t } = useTranslations();
  const intl = useIntl();
  const [, startTransition] = useTransition();

  const { open: isSidebarOpen } = useSidebar();
  useAtomValue(sessionlessEvidenceAwarenessDevVariantOverrideAtom);
  useAtomValue(sessionlessEvidenceAwarenessDevForceShowAtom);

  // User data for UserAccountMenu in footer
  const { data: userData } = useQueryUser(userDataSelector);

  const { isGlobalAdmin } = useCombinedOrganizationInfo();
  const { teamDetails } = useCombinedQueriesTeamGeneralInfo();
  const isMobile = useIsMobile();

  const { tutorialStepCurrentPage, onNextPage, panelOpen } = useTutorial();
  const shouldHighlightCommunity =
    tutorialStepCurrentPage?.enum === TemplatePages.GO_TO_TEMPLATE_COMMUNITY && panelOpen;

  const isAgentHomeV1Enabled = useIsAgentHomeV1Enabled();

  const enableTasks = IS_CLINICAL_TASKS_ENABLED();
  const isCallsEnabled = IS_CALLS_ENABLED() ?? false;

  // Hide the Comms *nav entry* inside an EHR Launch (SMART on FHIR — Cerner, Epic,
  // etc.). Scope is intentionally the nav entry only — this does NOT suppress the
  // full Comms surface (the `/comms` routes/CTAs elsewhere can still be reached).
  // `disableUiInEpicEhrLaunch` keeps the raw FHIR-auth signal (it also gates the
  // new-session UI below); note FHIR auth is also set by generic launches (e.g.
  // Cerner), so despite the name it is not Epic-only.
  const isEhrLaunch = useIsEhrLaunch();
  const disableUiInEpicEhrLaunch = useIsFhirAuth();

  // Self-serve users land on `/comms` (which then redirects to /comms/onboarding
  // for unonboarded users); without this gate they'd have no sidebar entry and
  // would have to type the URL manually. Mirrors the gate in /pages/comms/index.tsx.
  // The `!isEhrLaunch` prefix hides the nav entry in an EHR Launch.
  const enableCalls = !isEhrLaunch && (isCallsEnabled || IS_COMMS_SELF_SERVE_ENABLED());
  const enableProtocols = IS_PROTOCOLS_ENABLED();
  // Gates the Triage nav entry behind the triage POC flag (mirrors the BE
  // `release-scribe-enable-triage-poc`). Off → no sidebar entry, and the
  // /triage page itself redirects to /scribe.
  const enableTriage = useIsTriagePocEnabled();
  // Gates the Refills nav entry behind the repeats POC flag (mirrors the BE
  // `release-scribe-enable-repeats-poc`, which also gates the /repeat-reviews
  // router the page reads from). Off -> no sidebar entry, and /refills itself
  // redirects to /scribe. Sibling of `enableTriage` above.
  const enableRefills = useIsRepeatsPocEnabled();
  // One read for both sidebar branches, like `enableRefills` itself: the label
  // renders twice and the `id` is fixed, so two copies can only drift.
  const refillsLabel = intl.formatMessage({
    defaultMessage: 'Refills',
    id: 'zzv70DcujP',
    description: 'Sidebar entry for the repeat-prescription review queue',
  });
  // Off -> no sidebar entry, and /concierge itself redirects to /scribe.
  const enableConcierge = useIsConciergePocEnabled();
  const isRibbonSidebarEnabled = useIsRibbonSidebarEnabled();
  const isInsetDesignEnabled = IS_INSET_DESIGN_ENABLED();
  const isWorkspaceSwitcherEnabled = useIsWorkspaceSwitcherEnabled();
  const { isWhiteLabel } = useWhiteLabelSafe();
  // `isLoading`, not bare `data === undefined`: a settled-without-data state (a
  // 400 in the new-signup window, exhausted retries, or a pre-auth `skipToken`)
  // also leaves `data` undefined but must fall through to the logo, not pin the
  // skeleton forever.
  const { data: workspacesData, isLoading: workspacesLoading } = useQueryWorkspaces();
  const hasWorkspaces = (workspacesData?.workspaces?.length ?? 0) > 0;
  // The ribbon header has room for one identity affordance, so the switcher
  // takes the logo's slot instead of sitting below it. Gated off the flag, not
  // `collapsible === 'label-icons'`: the mobile sheet defaults collapsible to
  // `'icon'`, so keying off the flag keeps desktop and mobile consistent.
  const isSwitcherAllowed = isWorkspaceSwitcherEnabled && !disableUiInEpicEhrLaunch;
  const isSwitcherEligibleForHeader =
    isRibbonSidebarEnabled && !isWhiteLabel && isSwitcherAllowed;
  const showSwitcherInHeader =
    isSwitcherEligibleForHeader && !workspacesLoading && hasWorkspaces;
  // A skeleton (not the logo) covers the in-flight window; a logo->avatar
  // shape swap read as a loading bug.
  const showSwitcherSkeletonInHeader = isSwitcherEligibleForHeader && workspacesLoading;
  const showSwitcherBelowHeader =
    isSwitcherAllowed && (!isRibbonSidebarEnabled || isWhiteLabel);
  const isTauriDesktop = useMemo(() => {
    return isTauri();
  }, []);

  const externalLaunchContext = useAtomValue(externalLaunchContextAtom);

  const canCreateNewSession = !disableUiInEpicEhrLaunch;
  const { dismissMobileSidebar } = useMobilePanel();

  // Helper to close side panels when navigating
  const closeSidePanels = useCallback(() => {
    setSidebarOpenTab(undefined);
  }, []);

  // Check if a route is active
  const isRouteActive = useIsRouteActive();

  const { showEvidenceFocusedNav, shouldHideNavItemsForEvidenceSignup } =
    useEvidenceNavEnabled();

  // An evidence signup that hides nav items falls to the legacy branch even
  // with the ribbon flag on.
  const isRibbonLayout = isRibbonSidebarEnabled && !shouldHideNavItemsForEvidenceSignup;

  const isSidebarIAEnabled = useIsSidebarIAEnabled();
  const primaryNavMoreDescriptors = usePrimaryNavMoreDescriptors();

  const handleOnClickCloseSidePanels = useCallback(() => {
    startTransition(() => {
      closeSidePanels();
    });
    dismissMobileSidebar();
  }, [closeSidePanels, dismissMobileSidebar]);

  // Fixed-item descriptors for the collapse system (MUL-3702).
  // Collapsed items lose spotlight/tour affordances in the More dropdown (acceptable
  // at small screen sizes), but core navigation behaviour must match the real components.
  const router = useRouter();
  const baseChatHref = useBaseChatHref();
  const isOnHomeScreen = isAgentHomeRailActive(router.asPath, baseChatHref);
  const setPendingWorkHomeView = useSetAtom(pendingWorkHomeViewAtom);
  const setPendingWorkMode = useSetAtom(pendingWorkModeAtom);

  const primaryNavFixedDescriptors = useMemo(() => {
    const items = [];
    items.push({
      id: 'home' as const,
      label: intl.formatMessage(navV3Messages.home),
      icon: <House />,
      href: baseChatHref,
      testId: 'sidebar-link-home',
      isActive: isOnHomeScreen,
      onSelect: () => {
        if (baseChatHref === WORK_CHAT_HOME_PATH) {
          setPendingWorkHomeView('home');
          setPendingWorkMode(pendingWorkHomeMode);
        }
        dismissMobileSidebar();
      },
    });
    items.push({
      id: 'scribe' as const,
      label: intl.formatMessage({ defaultMessage: 'Scribe', id: '8mTxgS21Su' }),
      icon: <AudioLines />,
      href: '/scribe/sessions',
      testId: 'sidebar-link-view-sessions',
      isActive: router.asPath.startsWith('/scribe/session'),
      onSelect: handleOnClickCloseSidePanels,
    });
    if (IS_EVIDENCE_LIBRARY_ENABLED()) {
      items.push({
        id: 'library' as const,
        label: intl.formatMessage(navV3Messages.library),
        icon: <Library />,
        href: '/library',
        testId: 'sidebar-link-library',
        isActive: router.asPath.startsWith('/library'),
        onSelect: dismissMobileSidebar,
      });
    }
    if (
      enableTasks &&
      (isRibbonLayout || externalLaunchContext?.launchType !== ExternalLaunchType.OPENAPI)
    ) {
      items.push({
        id: 'tasks' as const,
        label: intl.formatMessage(navV3Messages.tasks),
        icon: <SquareCheck />,
        href: '/tasks',
        testId: 'sidebar-link-view-tasks',
        isActive: router.asPath.startsWith('/tasks'),
        onSelect: handleOnClickCloseSidePanels,
      });
    }
    items.push({
      id: 'templates' as const,
      label: intl.formatMessage({
        defaultMessage: 'Templates',
        id: 'PkY+9dwznr',
        description: 'Sidebar nav label for My Templates',
      }),
      icon: <Blocks />,
      href: '/scribe/my-templates',
      testId: 'sidebar-link-templates',
      isActive: router.asPath.startsWith('/scribe/my-templates'),
      onSelect: () => {
        trackTemplateLibraryViewed();
        handleOnClickCloseSidePanels();
      },
    });
    return items;
  }, [
    intl,
    baseChatHref,
    isOnHomeScreen,
    router.asPath,
    enableTasks,
    isRibbonLayout,
    externalLaunchContext,
    dismissMobileSidebar,
    handleOnClickCloseSidePanels,
    setPendingWorkHomeView,
    setPendingWorkMode,
  ]);

  const handleOnClickCalls = useCallback(() => {
    track({
      event_name: TrackingEventName.CommsEntryPointClicked,
      props: {
        entry_point: CommsEntryPoint.Sidenav,
        is_calls_enabled: isCallsEnabled,
      },
      options: { amplitude: false },
    });
    handleOnClickCloseSidePanels();
  }, [isCallsEnabled, handleOnClickCloseSidePanels]);

  // The primary nav order, shared verbatim by the ribbon and legacy layouts so
  // they can't drift apart; the lead and trailing settings stay at the call sites.
  const sharedNavItems = (
    <>
      {/* Tasks hides in an OPENAPI launch on the legacy sidebar (users can
          switch sessions there); the ribbon always shows it. */}
      {enableTasks &&
        (isRibbonLayout ||
          externalLaunchContext?.launchType !== ExternalLaunchType.OPENAPI) && (
          <TasksNavItem
            label={t('sidenav.tasks')}
            isActive={isRouteActive('/tasks')}
            onClick={handleOnClickCloseSidePanels}
          />
        )}
      <PatientsNavItem />
      {/* Templates is a flat item only on the ribbon; legacy keeps it in the
          My Library group below. */}
      {isRibbonLayout && (
        <NavItem
          label={t('sidenav.templates')}
          icon={<Blocks />}
          href="/scribe/my-templates"
          isActive={isRouteActive('/scribe/my-templates')}
          onClick={() => {
            startTransition(() => {
              trackTemplateLibraryViewed();
              closeSidePanels();
            });
            dismissMobileSidebar();
          }}
          testId="sidebar-link-templates"
        />
      )}
      {enableCalls && (
        <CallsNavItem
          label={t('sidenav.calls')}
          isActive={isRouteActive('/comms')}
          onClick={handleOnClickCalls}
        />
      )}
      {enableProtocols && <ProtocolsNavItem />}
      <TranslatorNavItem />
      <CodingDashboardNavItem />
      {enableTriage && (
        <TriageNavItem
          label={t('sidenav.triage')}
          isActive={isRouteActive('/triage')}
          onClick={handleOnClickCloseSidePanels}
        />
      )}
      {enableRefills && (
        <RefillsNavItem
          label={refillsLabel}
          isActive={isRouteActive('/refills')}
          onClick={handleOnClickCloseSidePanels}
        />
      )}
      {enableConcierge && (
        <ConciergeNavItem
          label={intl.formatMessage({
            defaultMessage: 'Concierge',
            id: '9G+x4ke1z0',
            description: 'Sidebar entry for the concierge agent request list',
          })}
          isActive={isRouteActive('/concierge')}
          onClick={handleOnClickCloseSidePanels}
        />
      )}
      <InboxNavItem />
    </>
  );

  return (
    <div
      className={cn(
        'group/sidebar-container relative flex size-full flex-col',
        isMobile && 'gap-2'
      )}
      data-testid="sidebar-container"
    >
      <SideNavHotkeys />
      {/* Sidebar Header with Heidi Logo */}
      {/* On the sessions route, ScribeLayout clears the desktop title bar and
          PageShell anchors this sidebar to that padded shell, so only add the
          8px / 16px breathing room below it. Other routes retain PageShell's
          viewport-fixed sidebar and therefore keep the full 40px / 48px local
          clearance used before SCR-7275. */}
      <SidebarHeader
        className={cn(
          isTauriDesktop &&
            getDesktopHeaderPaddingClass({ isInsetDesignEnabled, isShellAnchored })
        )}
      >
        {showSwitcherInHeader ? (
          <WorkspaceTopMenuMount inHeader />
        ) : showSwitcherSkeletonInHeader ? (
          <WorkspaceHeaderSlotSkeleton />
        ) : (
          <HeidiLogo />
        )}
      </SidebarHeader>

      {showSwitcherBelowHeader && (
        <SidebarGroup className="py-1">
          <WorkspaceTopMenuMount />
        </SidebarGroup>
      )}

      {/* Create Button - SidebarAction slot (legacy) */}
      {/* We MUST hide the new session button for smart on fhir launch for some */}
      {/* enterprises. MAKE SURE you keep this if you change anything around here. @andy */}
      {canCreateNewSession && !isRibbonSidebarEnabled && (
        <SidebarAction>
          <SidebarMenu>
            <NewSessionButton />
          </SidebarMenu>
        </SidebarAction>
      )}

      {canCreateNewSession && isRibbonSidebarEnabled && (
        <SidebarGroup className={cn('mb-1 mt-0', isMobile && 'mb-0')}>
          <SidebarMenu>
            <CreateButton />
          </SidebarMenu>
        </SidebarGroup>
      )}

      <SidebarContent className={cn(isRibbonSidebarEnabled && 'scrollbar-none')}>
        {/* Primary navigation group */}
        <SidebarGroup className={cn(isMobile && 'pt-0')}>
          <SidebarMenu>
            {isSidebarIAEnabled ? (
              <>
                {/* Sidebar IA rejig (MUL-3702): fixed items + overflow, collapsing
                    bottom-to-top into More as screen height decreases. */}
                <PrimaryNavRailProvider descriptors={primaryNavFixedDescriptors}>
                  <PrimaryNavFixedItem id="home">
                    <HomeNavItem />
                  </PrimaryNavFixedItem>
                  <PrimaryNavFixedItem id="scribe">
                    <ScribeNavItem />
                  </PrimaryNavFixedItem>
                  <PrimaryNavFixedItem id="library">
                    <LibraryNavItem />
                  </PrimaryNavFixedItem>
                  {enableTasks &&
                    (isRibbonLayout ||
                      externalLaunchContext?.launchType !==
                        ExternalLaunchType.OPENAPI) && (
                      <PrimaryNavFixedItem id="tasks">
                        <TasksNavItem
                          label={t('sidenav.tasks')}
                          isActive={isRouteActive('/tasks')}
                          onClick={handleOnClickCloseSidePanels}
                        />
                      </PrimaryNavFixedItem>
                    )}
                  <PrimaryNavFixedItem id="templates">
                    <NavItem
                      label={t('sidenav.templates')}
                      icon={<Blocks />}
                      href="/scribe/my-templates"
                      isActive={isRouteActive('/scribe/my-templates')}
                      onClick={() => {
                        startTransition(() => {
                          trackTemplateLibraryViewed();
                          closeSidePanels();
                        });
                        dismissMobileSidebar();
                      }}
                      testId="sidebar-link-templates"
                    />
                  </PrimaryNavFixedItem>
                  <PrimaryNavMoreGroup overflowDescriptors={primaryNavMoreDescriptors} />
                </PrimaryNavRailProvider>
              </>
            ) : (
              <>
                {isAgentHomeV1Enabled && <HomeNavItem />}
                {/* Lead only. Agent Home hides Evidence and leads with
                    Scribe/Library, so evidence-first only applies when it's off. */}
                {showEvidenceFocusedNav && !isAgentHomeV1Enabled ? (
                  <>
                    <EvidenceNavItem />
                    <ScribeNavItem />
                  </>
                ) : (
                  <>
                    <ScribeNavItem />
                    {isAgentHomeV1Enabled && <LibraryNavItem />}
                    {!isAgentHomeV1Enabled && <EvidenceNavItem />}
                  </>
                )}

                {isRibbonLayout ? (
                  <>
                    {sharedNavItems}
                    <SettingsGroupNavItems
                      startTransition={startTransition}
                      closeSidePanels={closeSidePanels}
                      isGlobalAdmin={!!isGlobalAdmin}
                      isMobile={isMobile}
                    />
                  </>
                ) : (
                  <ExpandableNavItemGroup
                    enabled={shouldHideNavItemsForEvidenceSignup}
                    testId="sidebar-expandable-more"
                  >
                    {/* The whole group folds behind "More" for the first-run
                        evidence collapse (`enabled`). */}
                    {sharedNavItems}

                    {/* My Library group */}
                    <SidebarGroup className="px-0">
                      <SidebarGroupLabel>{t('sidenav.myLibrary')}</SidebarGroupLabel>
                      <SidebarMenu>
                        {/* My Templates */}
                        <NavItem
                          label={t('sidenav.myTemplates')}
                          icon={<Blocks />}
                          href="/scribe/my-templates"
                          isActive={isRouteActive('/scribe/my-templates')}
                          onClick={() => {
                            startTransition(() => {
                              trackTemplateLibraryViewed();
                              closeSidePanels();
                            });
                            dismissMobileSidebar();
                          }}
                          testId="sidebar-link-my-templates"
                        />

                        {/* My Forms stays off the on-prem sidebar. Forms themselves
                            are available there — the template library's Forms tab
                            and the create-menu reach them — but this entry only
                            exists in the pre-redesign layout and the ribbon sidebar
                            drops it, so on-prem is not given a top-level slot the
                            new IA is already removing. */}
                        {!isOnPrem && (
                          <NavItem
                            label={t('sidenav.myForms')}
                            icon={<FileText />}
                            href="/scribe/my-forms"
                            isActive={isRouteActive('/scribe/my-forms')}
                            onClick={() => {
                              startTransition(() => {
                                trackFormLibraryViewed();
                                closeSidePanels();
                              });
                              dismissMobileSidebar();
                            }}
                            testId="sidebar-link-my-forms"
                          />
                        )}
                      </SidebarMenu>
                    </SidebarGroup>

                    {!isOnPrem && (
                      <SidebarGroup className="px-0">
                        <SidebarGroupLabel>{t('sidenav.community')}</SidebarGroupLabel>
                        <TemplateGroupNavItems
                          startTransition={startTransition}
                          closeSidePanels={closeSidePanels}
                          shouldHighlightCommunity={shouldHighlightCommunity}
                          onNextPage={onNextPage}
                          isTemplatesActive={isRouteActive('/templates')}
                        />
                      </SidebarGroup>
                    )}
                    <SidebarGroup data-testid="sidebar-group-settings" className="px-0">
                      <SettingsGroupNavItems
                        startTransition={startTransition}
                        closeSidePanels={closeSidePanels}
                        isGlobalAdmin={!!isGlobalAdmin}
                        isMobile={isMobile}
                      />
                    </SidebarGroup>
                  </ExpandableNavItemGroup>
                )}
              </>
            )}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      {/* Live transcription indicator - shows when navigated away from session */}
      {IS_LIVE_TRANSCRIPTION_INDICATOR_ENABLED() && (
        <LiveTranscriptionIndicator isSidebarOpen={isSidebarOpen} />
      )}

      {/* Footer */}
      <SidebarFooter>
        <SideNavFooter
          isMobile={isMobile}
          isSidebarOpen={isSidebarOpen}
          closeSidePanels={closeSidePanels}
          user={
            userData
              ? {
                  firstName: userData.first_name,
                  lastName: userData.last_name,
                  email: userData.email,
                  profilePictureUrl: userData.profile_picture_url,
                  shouldHideSensitiveInfo:
                    userData.should_hide_sensitive_info_from_sidebar,
                }
              : undefined
          }
          isGlobalAdmin={isGlobalAdmin}
          teamName={teamDetails?.team?.name}
        />
      </SidebarFooter>

      {GET_EVIDENCE_AWARENESS_POPUP_VARIANT() === 'floating' && (
        <SessionlessEvidenceAwarenessPopover variant="floating" />
      )}
    </div>
  );
}
